/*
 *  Copyright (C) GridGain Systems. All Rights Reserved.
 *  _________        _____ __________________        _____
 *  __  ____/___________(_)______  /__  ____/______ ____(_)_______
 *  _  / __  __  ___/__  / _  __  / _  / __  _  __ `/__  / __  __ \
 *  / /_/ /  _  /    _  /  / /_/ /  / /_/ /  / /_/ / _  /  _  / / /
 *  \____/   /_/     /_/   \_,__/   \____/   \__,_/  /_/   /_/ /_/
 */

import { Injectable } from '@angular/core';
import { Confirm } from '@app/common/modules/gmc-confirm/confirm.service';
import { Prompt } from '@app/common/modules/gmc-prompt/prompt.service';
import { bufferTimeToggledOnValue } from '@app/common/rxjs-operators/buffer-time-toggled-on-value';
import { extractError, isTruthy } from '@app/common/utils';
import {
  addDashboard,
  addDashboardErr,
  addWidget,
  addWidgetErr,
  cloneDashboard,
  dashboard,
  dashboardGroup,
  dashboardGroupErr,
  dashboardMetadataChange,
  dashboardMetadataChangeErr,
  fitWidgets,
  importDashboards,
  removeDashboard,
  removeDashboardErr,
  removeDashboardOk,
  removeWidget,
  removeWidgetErr,
  renameDashboard,
  updateDashboardGroup,
  widgetMetadataChange,
  widgetMetadataChangeErr,
} from '@app/core/ngrx/actions';
import { DashboardApiService } from '@app/core/services/dashboard.service';
import { AppState, Dashboard, Widget } from '@app/core/types';
import { MonitoringDashboardsService } from '@app/monitoring-dashboard/services/monitoring-dashboards.service';
import { generateLayout } from '@common/components/widgets-dashboard/domain/layout-generator';
import {
  sortLayoutItems,
  toGridLayout,
  toGridsterItem,
} from '@common/components/widgets-dashboard/domain/layout-utils';
import { plural } from '@common/utils/plural';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { cloneDeep } from 'lodash-es';
import { EMPTY, Observable, filter, forkJoin, of, throwError } from 'rxjs';
import { catchError, map, mergeMap, switchMap, take } from 'rxjs/operators';
import { v4 as uuid } from 'uuid';
import { dashboardById, dashboardGroupByName, dashboardGroupIdByName } from './app.selectors';

@Injectable()
export class DashboardEffects {
  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private prompt: Prompt,
    private confirm: Confirm,
    private dashboardService: DashboardApiService,
    private monitoringDashboardService: MonitoringDashboardsService,
  ) {}

  private updateDashboard =
    <T extends Action & { dashboardGroup: string; dashboardId: string }>(
      fn: (action: T) => (db: Dashboard) => Dashboard,
      err: (error: any) => Action,
    ) =>
    (source$: Observable<T>) =>
      source$.pipe(
        switchMap((action) =>
          this.store.select(dashboardById(action.dashboardGroup, action.dashboardId)).pipe(
            take(1),
            switchMap((db: Dashboard | undefined) =>
              !db
                ? throwError(
                    () => `There's no dashboard with id ${action.dashboardId} in group ${action.dashboardGroup}`,
                  )
                : of(db),
            ),
            map(fn(action)),
            switchMap((db) =>
              this.dashboardService
                .update(db)
                .pipe(map(() => dashboard({ dashboardGroupName: action.dashboardGroup, dashboard: db }))),
            ),
            catchError((error) => [err(error)]),
          ),
        ),
      );

  addDashboard$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addDashboard),
      switchMap((action) =>
        this.store.select(dashboardGroupIdByName(action.dashboardGroupName)).pipe(
          take(1),
          switchMap((dashboardGroupId) =>
            this.dashboardService.create(action.dashboard, dashboardGroupId).pipe(
              switchMap(() =>
                this.store.select(dashboardGroupByName(action.dashboardGroupName!)).pipe(
                  take(1),
                  filter(isTruthy),
                  switchMap((group) => {
                    if (group.workspaceIds.includes(action.dashboard.id)) return EMPTY;
                    return [
                      dashboard({
                        dashboardGroupName: action.dashboardGroupName,
                        dashboard: action.dashboard,
                      }),
                      dashboardGroup({
                        dashboardGroup: { ...group, workspaceIds: [...group.workspaceIds, action.dashboard.id] },
                      }),
                    ];
                  }),
                ),
              ),
              catchError((error) => [
                addDashboardErr({
                  error: extractError(error, `Can't create dashboard`),
                }),
              ]),
            ),
          ),
        ),
      ),
    ),
  );

  removeDashboard$ = createEffect(() =>
    this.actions$.pipe(
      ofType(removeDashboard),
      switchMap((a) =>
        this.confirm
          .confirm({
            title: `Remove ${a.dashboardIds.length} ${plural('dashboard', a.dashboardIds.length)}?`,
            message: `These dashboard configurations will be lost.`,
            dismissButtonLabel: `Cancel`,
            confirmButtonLabel: `Remove`,
          })
          .pipe(switchMap((v) => (v ? of(a) : EMPTY))),
      ),
      switchMap((action) => {
        return forkJoin(action.dashboardIds.map((dashboardId) => this.dashboardService.delete(dashboardId))).pipe(
          switchMap(() =>
            this.store.select(dashboardGroupByName(action.dashboardGroupName!)).pipe(
              take(1),
              filter(isTruthy),
              switchMap((group) => [
                updateDashboardGroup({
                  name: action.dashboardGroupName,
                  ids: group.workspaceIds.filter((v) => !action.dashboardIds.includes(v)),
                }),
                dashboardGroup({
                  dashboardGroup: {
                    ...group,
                    workspaceIds: group.workspaceIds.filter((v) => !action.dashboardIds.includes(v)),
                  },
                }),
                ...action.dashboardIds.map((dashboardId) =>
                  removeDashboardOk({
                    dashboardGroupName: action.dashboardGroupName,
                    dashboardId: dashboardId,
                  }),
                ),
              ]),
            ),
          ),
          catchError((error) => [
            removeDashboardErr({
              error: extractError(error, `Can't remove some dashboards`),
            }),
          ]),
        );
      }),
    ),
  );

  updateDashboard$ = createEffect(() =>
    this.actions$.pipe(
      ofType(dashboardMetadataChange),
      switchMap((action) =>
        this.dashboardService.update(action.dashboard).pipe(
          map(() =>
            dashboard({
              dashboardGroupName: action.dashboardGroupName,
              dashboard: action.dashboard,
            }),
          ),
          catchError((error) => [
            dashboardMetadataChangeErr({
              error: extractError(error, `Can't update dashboard`),
            }),
          ]),
        ),
      ),
    ),
  );

  renameDashboard$ = createEffect(() =>
    this.actions$.pipe(
      ofType(renameDashboard),
      switchMap((action) =>
        this.store.select(dashboardById(action.dashboardGroupName, action.dashboardId)).pipe(
          take(1),
          switchMap((db) =>
            !db
              ? EMPTY
              : this.prompt
                  .changeValue({
                    message: `Enter new dashboard name`,
                    value: db.metadata.title,
                  })
                  .pipe(
                    switchMap((title) => {
                      if (!title) return EMPTY;
                      const dashboard = {
                        ...db,
                        metadata: {
                          ...db.metadata,
                          title,
                        },
                      };
                      return of(dashboardMetadataChange({ dashboard, dashboardGroupName: action.dashboardGroupName }));
                    }),
                  ),
          ),
        ),
      ),
    ),
  );

  cloneDashboard$ = createEffect(() =>
    this.actions$.pipe(
      ofType(cloneDashboard),
      switchMap((action) =>
        this.store.select(dashboardById(action.dashboardGroupName, action.dashboardId)).pipe(
          take(1),
          switchMap((db) =>
            !db
              ? EMPTY
              : of(
                  addDashboard({
                    dashboardGroupName: action.dashboardGroupName,
                    dashboard: {
                      ...db,
                      id: uuid(),
                      metadata: {
                        ...cloneDeep(db.metadata),
                        title: db.metadata.title + ' (Copy)',
                      },
                      widgets: db.widgets.map((w) => ({ ...w, id: uuid() })),
                    },
                  }),
                ),
          ),
        ),
      ),
    ),
  );

  importDashboard$ = createEffect(() =>
    this.actions$.pipe(
      ofType(importDashboards),
      switchMap((action) => {
        const firstDashboard = action.dashboardDocument.dashboards.at(0);
        return !firstDashboard
          ? EMPTY
          : of(
              addDashboard({
                dashboardGroupName: action.dashboardGroupName,
                dashboard: {
                  ...firstDashboard,
                  id: uuid(),
                },
              }),
            );
      }),
    ),
  );

  removeWidget$ = createEffect(() =>
    this.actions$.pipe(
      ofType(removeWidget),
      this.updateDashboard(
        (action) => (db) => ({
          ...db,
          widgets: db.widgets.filter((w) => w.id !== action.widgetId),
        }),
        (error: any) =>
          removeWidgetErr({
            error: extractError(error, `Can't remove widget`),
          }),
      ),
    ),
  );

  widgetChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(widgetMetadataChange),
      bufferTimeToggledOnValue(200),
      mergeMap((actionBuffer) =>
        of(actionBuffer[0]).pipe(
          this.updateDashboard(
            (action) => (db) => ({
              ...db,
              widgets: db.widgets.map((w) => {
                const action = actionBuffer.find((a) => w.id === a.widgetId);
                return action ? { ...w, metadata: action.metadata } : w;
              }),
            }),
            (error: any) =>
              widgetMetadataChangeErr({
                error: extractError(error, `Can't change widget`),
              }),
          ),
        ),
      ),
    ),
  );

  addWidget$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addWidget),
      this.updateDashboard(
        (action) => (db) => {
          let widgets = db.widgets;
          const widget = widgets.find((w) => w.id === action.widget.id);
          if (widget) {
            widgets = widgets.map((w) => (w.id === action.widget.id ? action.widget : w));
          } else {
            widgets = [
              ...widgets.map((widget) => {
                //  pull down widgets
                if (widget.metadata.gridster.x < action.widget.metadata.gridster.cols) {
                  const gridster = {
                    ...widget.metadata.gridster,
                    y: widget.metadata.gridster.y + action.widget.metadata.gridster.rows,
                  };
                  return {
                    ...widget,
                    metadata: { ...widget.metadata, gridster },
                  } as Widget;
                }
                return widget;
              }),
              action.widget,
            ];
          }

          return { ...db, widgets };
        },
        (error: any) =>
          addWidgetErr({
            error: extractError(error, `Can't add widget`),
          }),
      ),
    ),
  );

  fitWidgets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fitWidgets),
      switchMap((action) =>
        this.store.select(dashboardById(action.dashboardGroupName, action.dashboardId)).pipe(
          take(1),
          switchMap((db) => {
            if (!db || db.widgets.length === 0) return EMPTY;

            const MAX_WIDGETS_ON_VIEWPORT = 12;
            const pages = Math.ceil(db.widgets.length / MAX_WIDGETS_ON_VIEWPORT);
            const newLayout = generateLayout(db.widgets.length, pages);

            const layoutItems = sortLayoutItems(
              db.widgets.map((widget) => ({
                id: widget.id,
                ...toGridLayout(widget.metadata.gridster),
              })),
            ).map((item, i) => ({
              ...item,
              ...newLayout[i],
            }));

            const widgets = db.widgets.map((widget, i) => {
              const layout = layoutItems.find((it) => it.id === widget.id)!;
              return {
                ...widget,
                metadata: {
                  ...widget.metadata,
                  gridster: {
                    ...widget.metadata.gridster,
                    ...toGridsterItem(layout),
                  },
                },
              } as Widget;
            });
            const dashboard = { ...db, widgets };
            return of(dashboardMetadataChange({ dashboard, dashboardGroupName: action.dashboardGroupName }));
          }),
        ),
      ),
    ),
  );

  updateDashboardGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateDashboardGroup),
      mergeMap((action) =>
        this.store.select(dashboardGroupByName(action.name)).pipe(
          take(1),
          filter(isTruthy),
          map((group) => ({ ...group, workspaceIds: action.ids })),
          switchMap((group) =>
            this.monitoringDashboardService.updateDashboardGroup(group).pipe(
              map(() => dashboardGroup({ dashboardGroup: group })),
              catchError((err) => [
                dashboardGroupErr({
                  error: extractError(err, `Can't update dashboard group`),
                }),
              ]),
            ),
          ),
        ),
      ),
    ),
  );
}
